<template>
  <TableComp
    :headers="headers"
    name="Tasks"
    :status="tasks.status"
    :pagination="tasks.pagination"
    :sort="tasks.sort"
    :number-of-items="tasks.list.length"
    :header-actions="getHeaderActions()"
    empty-image="tasks"
    :empty-bold-message="$t('global.no-tasks-bold-message')"
    :empty-message="$t('global.no-tasks-message')"
    :empty-actions="getEmptyActions()"
  >
    <template v-slot:desktop>
      <TableRow v-for="cron in tasks.list" :key="cron.id">
        <td class="align-middle">{{ cron.script }}</td>
        <td class="align-middle text-center">{{ getFrequency(cron) }}</td>
        <td class="align-middle text-center">
          <Toggle
            v-model="cron.enabled"
            @change="onToggle($event, cron)"
            :transitioning="isSubmitting(cron.id)"
            :disabled="isSubmitting(cron.id) || tasks.status.isUpdating"
          />
        </td>
        <template #actionItems>
          <ActionIcons :actions="getTaskActions(cron)" />
        </template>
      </TableRow>
    </template>
    <template v-slot:mobile>
      <TableMobileRow v-for="cron in tasks.list" v-bind:key="cron.id">
        <template v-slot:header>
          <div class="d-flex align-items-center">
            <div class="flex-fill">
              {{ cron.script }}
            </div>
            <div>
              <ActionIcons :actions="getTaskActions(cron)" />
            </div>
          </div>
        </template>
        <template v-slot:content>
          <div class="row">
            <div class="col">{{ $t("global.frequency") }}:</div>
            <div class="col">{{ getFrequency(cron) }}</div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.status") }}:</div>
            <div class="col">
              <Toggle
                v-model="cron.enabled"
                @change="onToggle($event, cron)"
                :transitioning="isSubmitting(cron.id)"
                :disabled="isSubmitting(cron.id) || tasks.status.isUpdating"
              />
            </div>
          </div>
        </template>
      </TableMobileRow>
    </template>
  </TableComp>
</template>

<script>
import ActionIcons from "@/components/display/ActionIcons";
import NavLink from "@/classes/NavLink";
import { ref } from "@vue/composition-api";
import SiteAddCron from "@/components/smart/modals/SiteAddCron";
import SiteDeleteCron from "@/components/smart/modals/SiteDeleteCron";
import store from "@/store";
import TableComp from "@/components/display/table/TableComp";
import TableHeader from "@/classes/TableHeader";
import TableMobileRow from "@/components/display/table/TableMobileRow";
import TableRow from "@/components/display/table/TableRow";
import Toggle from "@/components/display/Toggle";

export default {
  name: "SiteCronTasksTable",
  props: {
    resourceName: {
      type: String,
      required: true,
    },
    site: {
      type: Object,
      required: true,
    },
    tasks: Object,
  },
  components: {
    ActionIcons,
    TableComp,
    TableMobileRow,
    TableRow,
    Toggle,
  },
  setup(props, { root }) {
    const submits = ref([]);
    const headers = [
      new TableHeader(root.$t("global.script"), undefined),
      new TableHeader(root.$t("global.frequency"), "justify-content-center"),
      new TableHeader(root.$t("global.status"), "justify-content-center"),
      new TableHeader(),
    ];

    const addSubmitting = (cronId) => {
      const id = Number(cronId);
      const index = submits.value.findIndex((item) => item === id);
      if (index < 0) {
        submits.value.push(id);
      }
    };

    const getEmptyActions = () => [
      new NavLink(
        root.$t("global.add-task"),
        null,
        openAddForm,
        false,
        "primaryOutline",
        null,
        props.tasks.status.isLoading
      ),
    ];

    const getHeaderActions = () => [
      new NavLink(
        "Add Task",
        "add",
        openAddForm,
        false,
        null,
        "epsilon",
        props.tasks.status.isLoading
      ),
    ];

    const getFrequency = (cron) => {
      const numberReg = /^\d+$/;
      if (
        cron.minute.substring(0, 2) === "*/" &&
        cron.hour === "*" &&
        cron.day === "*" &&
        cron.month === "*"
      ) {
        return root.$t("global.every-x-minutes", { minutes: cron.minute.substring(2) });
      }

      if (
        numberReg.test(cron.minute) &&
        cron.hour === "*" &&
        cron.day === "*" &&
        cron.month === "*"
      ) {
        return root.$t("global.every-hour");
      }

      if (
        numberReg.test(cron.minute) &&
        numberReg.test(cron.hour) &&
        cron.day === "*" &&
        cron.month === "*"
      ) {
        return root.$t("global.every-day");
      }

      if (
        numberReg.test(cron.minute) &&
        numberReg.test(cron.hour) &&
        numberReg.test(cron.day) &&
        cron.month === "*"
      ) {
        return root.$t("global.every-month");
      }
      return root.$t("global.cron-format", cron);
    };

    const getTaskActions = (cron) => [
      new NavLink(
        "Remove Task",
        "trash",
        () => openDeleteForm(cron),
        false,
        null,
        "epsilon",
        props.tasks.status.isUpdating
      ),
    ];

    const isSubmitting = (cronId) =>
      submits.value.findIndex((item) => item === Number(cronId)) >= 0;

    const openAddForm = () =>
      store.dispatch("modals/open", {
        template: SiteAddCron,
        data: { resourceName: props.resourceName, site: props.site },
      });

    const openDeleteForm = (cron) =>
      store.dispatch("modals/open", {
        template: SiteDeleteCron,
        data: { cron: cron, resourceName: props.resourceName, site: props.site },
      });

    const onToggle = (enable, cron) => {
      const name = props.resourceName;
      addSubmitting(cron.id);
      store
        .dispatch("siteCron/updateTask", {
          siteId: props.site.id,
          cronId: cron.id,
          data: { enable },
        })
        .then(() => {
          store.dispatch("toasts/addToast", {
            variant: "info",
            message: root.$t("toasts.cron-update-success-message", {
              action: root.$t(enable ? "global.enabled" : "global.disabled").toLowerCase(),
              name: name,
              script: cron.script,
            }),
          });
        })
        .catch(() => {
          store.dispatch("toasts/addToast", {
            variant: "error",
            message: root.$t("toasts.cron-update-error-message", {
              action: root.$t(enable ? "global.enable" : "global.disable").toLowerCase(),
              name: name,
              script: cron.script,
            }),
          });
        })
        .finally(() => {
          removeSubmitting(cron.id);
        });
    };

    const removeSubmitting = (cronId) => {
      const index = submits.value.findIndex((item) => item === Number(cronId));
      if (index >= 0) {
        submits.value.splice(index, 1);
      }
    };

    return {
      getFrequency,
      getEmptyActions,
      getHeaderActions,
      getTaskActions,
      headers,
      isSubmitting,
      onToggle,
      openDeleteForm,
    };
  },
};
</script>
